/**
 * The permanent left sidebar.
 *
 * One instance for the whole application, rendered by `AppShell`, so it stays
 * visually stable across navigation instead of each route mounting its own
 * frame. The three bands - global, active project, support - are what make the
 * numbered project navigation legible: 01..06 are steps through one project's
 * work, and they only exist while a project is open.
 *
 * The active-project band is driven entirely by the route. There is no "last
 * opened project" memory, because there is no store to hold it and inventing
 * one would show a project the user is not looking at.
 */

import { useCallback } from "react";
import { NavLink, useMatch } from "react-router-dom";
import { getProject } from "@/api/client";
import {
  BrandMark,
  BrandWordmark,
  IconFace,
  IconFolder,
  IconHelp,
  IconHome,
  IconInfo,
  IconPerson,
  IconPlusSquare,
  IconSettings,
} from "@/components/icons";
import { subjectTypeLabel } from "@/i18n/terms";
import { PROJECT_SECTIONS } from "@/features/projects/projectNav";
import { useAsync } from "@/lib/useAsync";
import { paths } from "./paths";
import { VersionCard } from "./VersionCard";
import { useAuth } from "@/features/auth/AuthGate";

const GLOBAL_NAV = [
  { to: paths.home(), label: "Ana Sayfa", icon: IconHome, end: true },
  { to: paths.projects(), label: "Projelerim", icon: IconFolder, end: true },
  { to: paths.newProject(), label: "Yeni Proje", icon: IconPlusSquare, end: false },
];

const SUPPORT_NAV = [
  { to: paths.help(), label: "Yardım", icon: IconHelp },
  { to: paths.settings(), label: "Ayarlar", icon: IconSettings },
  { to: paths.about(), label: "Hakkında", icon: IconInfo },
];

function ActiveProject({ projectId }: { projectId: string }) {
  const fetcher = useCallback(
    (signal: AbortSignal) => getProject(projectId, { signal }),
    [projectId],
  );
  const { state } = useAsync(fetcher);
  const project = state.status === "success" ? state.data : null;

  return (
    <div className="sidebar__project">
      <p className="sidebar__eyebrow">Aktif proje</p>
      {/* The name is whatever the user typed. React escapes it; nothing here
          builds markup from it.

          The dot means one thing: this is the project you currently have open.
          It is **not** a health, success, coverage or reconstruction signal -
          it is green because the design system's active token is green, and it
          appears on exactly the routes that have a project context.

          It is decoration for assistive technology (`aria-hidden`), because
          the eyebrow above already says "Aktif proje" in words, and the block
          keeps its existing background and rule. Nobody has to see the colour
          to know which project is open. */}
      <p className="sidebar__project-name">
        <span className="sidebar__active-dot" aria-hidden="true" />
        {project ? project.name : "…"}
      </p>
      {project && (
        <ul className="sidebar__facts">
          <li>
            <IconPerson size={15} />
            {subjectTypeLabel(project.subject_type)}
          </li>
          {/* A second descriptor only when the backend actually has one. The
              mockup shows two lines; a fabricated one is not a line. */}
          {project.target_output && (
            <li>
              <IconFace size={15} />
              {project.target_output}
            </li>
          )}
        </ul>
      )}

      <ul className="sidebar__steps">
        {/* The same six sections the project header's rail renders. One
            definition, so the two surfaces cannot drift apart again. */}
        {PROJECT_SECTIONS.map((section) => (
          <li key={section.key}>
            <NavLink
              to={section.path(projectId)}
              end={section.end}
              className="sidebar__step"
            >
              <span className="sidebar__step-n">{section.n}</span>
              <span>{section.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Who is signed in, and the way out. */
function SignedInAs() {
  const { user, signOut } = useAuth();
  return (
    <div className="sidebar__account">
      <p className="sidebar__account-name" title={user.email}>
        {user.display_name}
      </p>
      <button type="button" className="sidebar__signout" onClick={() => void signOut()}>
        Çıkış Yap
      </button>
    </div>
  );
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const match = useMatch("/projeler/:projectId/*");
  const indexMatch = useMatch("/projeler/:projectId");
  const projectId = match?.params.projectId ?? indexMatch?.params.projectId;
  // "yeni" is the new-project route, not a project id.
  const activeProjectId = projectId && projectId !== "yeni" ? projectId : null;

  return (
    <nav className="sidebar" aria-label="Ana gezinme" onClick={onNavigate}>
      <div className="sidebar__brand">
        <BrandMark size={30} id="sidebar" />
        <BrandWordmark />
      </div>

      <ul className="sidebar__nav">
        {GLOBAL_NAV.map((item) => (
          <li key={item.to}>
            <NavLink to={item.to} end={item.end} className="sidebar__link">
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>

      {activeProjectId && <ActiveProject projectId={activeProjectId} />}

      <ul className="sidebar__nav sidebar__nav--support">
        {SUPPORT_NAV.map((item) => (
          <li key={item.to}>
            <NavLink to={item.to} className="sidebar__link">
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>

      <SignedInAs />
      <VersionCard />
    </nav>
  );
}
